import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Button, Card, GovernmentIdentity, TextInput } from '@ministry/ui/internal';

import { requestPasswordReset } from '../lib/auth.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';

/**
 * SCREEN_SPECS.md I4's request step. **Built against the live server, not the spec's own
 * prose**: `POST /auth/password/reset-request` takes `{codePrefix, username}` — the SAME
 * two identifying fields I1's login form uses — and has no email field in its schema at
 * all (confirmed live; see `lib/auth.ts`'s `requestPasswordReset` comment). SCREEN_SPECS
 * calls this screen "email-based" with an email input; that would 422 on every submission
 * against this server, so this screen matches the real contract and the divergence is
 * flagged in the handback rather than shipped as a dead field.
 *
 * The success state is deliberately generic regardless of whether the account exists —
 * verified live: `officer` and a nonexistent username both answer `{accepted: true}` —
 * the same enumeration-oracle discipline I1 and the public tracking lookup already use.
 */
export function PasswordResetRequestPage(): React.JSX.Element {
  const t = useT();
  const [codePrefix, setCodePrefix] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [touched, setTouched] = React.useState<{ codePrefix?: boolean; username?: boolean }>({});

  const mutation = useMutation({ mutationFn: requestPasswordReset });

  const codePrefixError =
    touched.codePrefix && codePrefix.trim() === ''
      ? t('login.required')
      : touched.codePrefix && !/^[A-Za-z]{2,6}$/.test(codePrefix.trim())
        ? t('login.ministry.invalid')
        : undefined;
  const usernameError = touched.username && username.trim() === '' ? t('login.required') : undefined;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ codePrefix: true, username: true });
    if (!/^[A-Za-z]{2,6}$/.test(codePrefix.trim()) || username.trim() === '') return;
    mutation.mutate({ codePrefix: codePrefix.trim().toUpperCase(), username: username.trim() });
  };

  const isNetworkFailure = mutation.error instanceof NetworkError || (mutation.error instanceof ApiError && mutation.error.retryable);

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <header className="flex items-center justify-between gap-4 px-4 py-3">
        <GovernmentIdentity />
        <LocaleToggle />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4">
        <Card className="w-full max-w-sm p-6">
          <h1 className="mb-2 text-xl font-semibold text-text-heading">{t('passwordReset.request.title')}</h1>
          <p className="mb-4 text-sm text-text-muted">{t('passwordReset.request.body')}</p>

          {mutation.isSuccess ? (
            <AlertBanner variant="info" title={t('passwordReset.request.success.title')}>
              {t('passwordReset.request.success.body')}
            </AlertBanner>
          ) : (
            <>
              {mutation.isError ? (
                <div className="mb-4">
                  <AlertBanner
                    variant="critical"
                    title={isNetworkFailure ? t('passwordReset.request.error.network') : t('passwordReset.request.error.generic')}
                    action={
                      isNetworkFailure ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => mutation.mutate({ codePrefix: codePrefix.trim().toUpperCase(), username: username.trim() })}
                        >
                          {t('common.retry')}
                        </Button>
                      ) : undefined
                    }
                  />
                </div>
              ) : null}

              <form onSubmit={submit} noValidate className="flex flex-col gap-4">
                <TextInput
                  id="reset-request-ministry"
                  label={t('login.ministry.label')}
                  name="organization"
                  autoCapitalize="characters"
                  value={codePrefix}
                  onChange={(e) => setCodePrefix(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, codePrefix: true }))}
                  errorText={codePrefixError}
                  helperText={t('login.ministry.hint')}
                  required
                />
                <TextInput
                  id="reset-request-username"
                  label={t('login.username.label')}
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
                  errorText={usernameError}
                  required
                />
                <Button type="submit" loading={mutation.isPending} loadingLabel={t('passwordReset.request.submitting')}>
                  {t('passwordReset.request.submit')}
                </Button>
              </form>
            </>
          )}

          <Link to="/login" className="mt-4 inline-block text-sm font-semibold text-text-link underline">
            {t('mfa.backToLogin')}
          </Link>
        </Card>
      </div>
    </div>
  );
}
